/**
 * Offline queue for idempotent progress-style writes.
 *
 * Mirrors the server's `GREATEST()` upsert semantics documented on
 * `portalApi.postLessonProgress` (frontend/web-ide/src/services/portal.ts):
 * a momentary connectivity blip must never lose progress, and replaying two
 * queued updates for the SAME resource should keep the higher value rather
 * than stomp it with a stale one. `mergeNumericField` names the body field
 * to take the max of when a new queue entry collides with an existing one
 * under the same `key`.
 */

export interface QueuedRequest {
  path: string;
  method: 'POST' | 'PATCH' | 'PUT';
  body: Record<string, unknown>;
  /** Dedupe + storage key — e.g. `progress:${courseSlug}:${lessonSlug}`. */
  key: string;
  /** Body field to GREATEST()-merge on collision, e.g. `watched_pct`. */
  mergeNumericField?: string;
  /**
   * Body fields that are additive deltas (e.g. `seeks`). On collision these are
   * SUMMED rather than overwritten, so queued offline deltas are not lost.
   */
  sumNumericFields?: string[];
  sumNumericCaps?: Record<string, number>;
  queuedAt: number;
}

export interface OfflineQueueStore {
  all(): QueuedRequest[];
  /** Insert, or merge into the existing entry with the same `key`. */
  upsert(item: QueuedRequest): void;
  remove(key: string): void;
  clear(): void;
}

function mergeEntries(existing: QueuedRequest, incoming: QueuedRequest): QueuedRequest {
  const field = incoming.mergeNumericField ?? existing.mergeNumericField;
  let merged: QueuedRequest = incoming;
  if (field) {
    const existingValue = existing.body[field];
    const incomingValue = incoming.body[field];
    if (typeof existingValue === 'number' && typeof incomingValue === 'number') {
      merged = {
        ...incoming,
        body: { ...incoming.body, [field]: Math.max(existingValue, incomingValue) },
      };
    }
  }
  const sumFields = incoming.sumNumericFields ?? existing.sumNumericFields ?? [];
  if (sumFields.length === 0) return merged;
  const body = { ...merged.body };
  for (const f of sumFields) {
    const a = existing.body[f];
    const b = incoming.body[f];
    const cap = (incoming.sumNumericCaps ?? existing.sumNumericCaps)?.[f] ?? Infinity;
    const total = Math.min(cap, (typeof a === 'number' ? a : 0) + (typeof b === 'number' ? b : 0));
    if (total > 0) body[f] = total;
  }
  return { ...merged, body, sumNumericFields: sumFields, sumNumericCaps: incoming.sumNumericCaps ?? existing.sumNumericCaps };
}

/** Default in-memory store. Callers that need cross-launch persistence on
 *  native (AsyncStorage/MMKV) should implement `OfflineQueueStore` themselves
 *  and pass it to `createApiClient` — this package has no storage opinion. */
export function createInMemoryQueueStore(): OfflineQueueStore {
  const items = new Map<string, QueuedRequest>();
  return {
    all: () => Array.from(items.values()).sort((a, b) => a.queuedAt - b.queuedAt),
    upsert: (item) => {
      const existing = items.get(item.key);
      items.set(item.key, existing ? mergeEntries(existing, item) : item);
    },
    remove: (key: string) => {
      items.delete(key);
    },
    clear: () => items.clear(),
  };
}
